import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

function NotificationCard({ item }) {
  const isSession = item.type === 'session';
  let timeAgo = '';
  try {
    timeAgo = formatDistanceToNow(item.timestamp, { addSuffix: true });
  } catch(e) {
    timeAgo = 'recently';
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group"
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-full border border-neutral-800 bg-neutral-950 text-amber-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
        <SafeIcon icon={isSession ? FiIcons.FiUserPlus : FiIcons.FiMessageSquare} className="text-sm" />
      </div>

      <div className="w-[calc(100%-3.5rem)] md:w-[calc(50%-2.5rem)] p-5 rounded-xl border border-neutral-800/50 bg-neutral-900/40 hover:bg-neutral-900/60 transition-colors shadow-sm">
        <div className="flex flex-wrap gap-2 items-center justify-between mb-2">
          <span className="text-xs font-mono text-amber-500/80 uppercase">
            {isSession ? 'New Commitment' : `Stage ${item.data.stage_number} Completed`}
          </span>
          <span className="text-xs text-neutral-500 font-mono flex items-center gap-1">
            <SafeIcon icon={FiIcons.FiClock} className="text-neutral-600" /> {timeAgo}
          </span>
        </div>
        <p className="text-neutral-300 text-sm leading-relaxed">
          {isSession ? (
            <>An anonymous user started a new ritual in the domain of <strong className="text-neutral-100 font-medium tracking-wide">"{item.data.goal_domain}"</strong>.</>
          ) : (
            <>User progressed past <strong className="text-neutral-100 font-medium tracking-wide">"{item.data.stage_name}"</strong>.</>
          )}
        </p>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);

  // FIX: Use global regex for date normalization
  const fetchDashboardData = useCallback(async () => {
    try {
      const [sessionsRes, stagesRes] = await Promise.all([
        api.getSessions(),
        api.getStageResponses()
      ]);

      const sessions = (sessionsRes.data || []).map((s, i) => ({
        id: `session-${s.id || i}`,
        type: 'session',
        timestamp: new Date(s.created_at.replace(/ /g, 'T')),
        data: s
      }));

      const stages = (stagesRes.data || []).map((s, i) => ({
        id: `stage-${s.id || i}`,
        type: 'stage',
        timestamp: new Date(s.created_at.replace(/ /g, 'T')),
        data: s
      }));

      const combined = [...sessions, ...stages].sort((a, b) => b.timestamp - a.timestamp);
      setNotifications(combined);
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const runConnectionTest = async () => {
    setIsTesting(true);
    try {
      // FIX: Standardize SQL-compatible date generation using global regex
      const getFormattedDate = () => new Date().toISOString().slice(0, 19).replace(/T/g, ' ');

      const session = await api.createSession({
        app_user_id: 999,
        goal_domain: 'Connection Test Domain',
        created_at: getFormattedDate()
      });

      if (session && session.id) {
        await api.createStageResponse({
          session_id: session.id,
          stage_number: 1,
          stage_name: 'Stage 1: Connection Test',
          user_response: 'This is an automated test confirming the frontend is talking to the backend successfully.',
          created_at: getFormattedDate()
        });
      }

      await fetchDashboardData();
      alert("Connection successful! Test data has been written and read from the backend.");
    } catch (error) {
      console.error("Connection test failed:", error);
      alert(`Connection failed: ${error.message}\nCheck your API Key and Network tab.`);
    } finally {
      setIsTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32 text-neutral-500">
        <span className="inline-block w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pt-8 pb-24 px-2">
      <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6 text-center md:text-left">
        <div>
          <h2 className="text-3xl font-serif text-amber-500 mb-3">Activity Feed</h2>
          <p className="text-neutral-400">Live updates from users forging their commitments.</p>
        </div>
        <button 
          onClick={runConnectionTest}
          disabled={isTesting}
          className="px-4 py-2 bg-neutral-900 border border-neutral-800 text-neutral-300 rounded-lg hover:bg-neutral-800 hover:text-amber-500 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50 font-mono"
        >
          {isTesting ? (
            <span className="inline-block w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></span>
          ) : (
            <SafeIcon icon={FiIcons.FiDatabase} />
          )}
          {isTesting ? 'TESTING...' : 'RUN CONNECTION TEST'}
        </button>
      </div>

      <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-[1px] before:bg-gradient-to-b before:from-transparent before:via-neutral-800 before:to-transparent">
        {notifications.map((item) => (
          <NotificationCard key={item.id} item={item} />
        ))}

        {notifications.length === 0 && (
          <div className="text-center text-neutral-600 py-12 font-serif italic text-lg">
            "The ritual awaits its first participants."
          </div>
        )}
      </div>
    </div>
  );
}